import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { actionService } from '../services/api';
import { toast } from 'sonner';
import { Plus, Clock, AlertCircle, UserPlus } from 'lucide-react';
import api from '../services/api';

const AddActionDialog = ({ bénéficiaireId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitableUsers, setInvitableUsers] = useState([]);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [formData, setFormData] = useState({
    apprenant_id: bénéficiaireId,
    type_action: 'rdv',
    titre: '',
    date: new Date().toISOString().split('T')[0],
    heure_debut: '09:00',
    heure_fin: '10:00',
    zone_geographique: 'Saint-Pierre',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      loadInvitableUsers();
    }
  }, [open]);

  const loadInvitableUsers = async () => {
    try {
      const response = await api.get('/users/invitables');
      setInvitableUsers(response.data || []);
    } catch (error) {
      console.error('Erreur chargement utilisateurs invitables:', error);
      // Fallback: essayer l'ancienne route pour les admins
      try {
        const fallbackResponse = await api.get('/users');
        const invitables = (fallbackResponse.data || []).filter(u => 
          ['financeur', 'referent', 'animateur'].includes(u.role) && u.actif !== false
        );
        setInvitableUsers(invitables);
      } catch (e) {
        console.error('Fallback aussi échoué:', e);
      }
    }
  };

  const handleToggleInvite = (userId) => {
    setSelectedInvites(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'financeur': return 'bg-purple-100 text-purple-700';
      case 'referent': return 'bg-green-100 text-green-700';
      case 'animateur': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'financeur': return 'Financeur';
      case 'referent': return 'Référent';
      case 'animateur': return 'Intervenant';
      default: return role;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Créer l'action SANS présence ni signatures
      // Ces éléments seront gérés séparément après l'heure de début
      await actionService.create({
        ...formData,
        apprenant_id: bénéficiaireId,
        invites_ids: selectedInvites,
        statut_presence: 'non_defini', // Statut initial (pas absent d'office)
        signature_intervenant: null,
        signature_apprenant: null
      });
      
      toast.success('Action programmée avec succès');
      setOpen(false);
      setFormData({
        apprenant_id: bénéficiaireId,
        type_action: 'rdv',
        titre: '',
        date: new Date().toISOString().split('T')[0],
        heure_debut: '09:00',
        heure_fin: '10:00',
        zone_geographique: 'Saint-Pierre',
        notes: ''
      });
      setSelectedInvites([]);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-action-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" />
          Programmer un RDV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>Nouveau Rendez-vous</DialogTitle>
          <DialogDescription>Programmez un rendez-vous individuel avec le bénéficiaire</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type d'action</Label>
            <Select value={formData.type_action} onValueChange={(val) => setFormData({ ...formData, type_action: val })}>
              <SelectTrigger data-testid="action-type-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rdv">Rendez-vous Individuel</SelectItem>
                <SelectItem value="entretien">Entretien de suivi</SelectItem>
                <SelectItem value="accompagnement">Accompagnement</SelectItem>
                <SelectItem value="bilan">Bilan</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="titre">Titre</Label>
            <Input
              id="titre"
              data-testid="action-titre-input"
              value={formData.titre}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              required
              className="rounded-xl"
              placeholder="Ex: Entretien de suivi mensuel"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                data-testid="action-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="debut">Début</Label>
              <Input
                id="debut"
                data-testid="action-debut-input"
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin">Fin</Label>
              <Input
                id="fin"
                data-testid="action-fin-input"
                type="time"
                value={formData.heure_fin}
                onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="zone">Zone</Label>
            <Select value={formData.zone_geographique} onValueChange={(val) => setFormData({ ...formData, zone_geographique: val })}>
              <SelectTrigger data-testid="action-zone-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                <SelectItem value="Saint-Denis">Saint-Denis</SelectItem>
                <SelectItem value="Le Port">Le Port</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="action-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="rounded-xl"
              placeholder="Notes supplémentaires..."
            />
          </div>

          {/* Sélection des invités */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Inviter des participants <span className="text-xs text-slate-500">(optionnel)</span>
            </Label>
            <div className="border rounded-xl p-3 max-h-[150px] overflow-y-auto bg-slate-50">
              {invitableUsers.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {invitableUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedInvites.includes(user.id) 
                          ? 'bg-purple-100 border border-purple-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleInvite(user.id)}
                      data-testid={`action-invite-user-${user.id}`}
                    >
                      <input 
                        type="checkbox"
                        checked={selectedInvites.includes(user.id)}
                        onChange={() => handleToggleInvite(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <span className="font-medium text-sm">{user.prenom} {user.nom}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedInvites.length > 0 && (
              <p className="text-xs text-purple-600">{selectedInvites.length} invité(s) - Ils recevront une notification</p>
            )}
          </div>

          {/* Information sur le processus d'émargement */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Processus d'émargement</p>
                <p className="text-xs text-blue-700 mt-1">
                  L'émargement sera disponible une fois l'heure du rendez-vous passée. 
                  L'intervenant pourra alors valider la présence, puis le bénéficiaire 
                  recevra une demande de signature sur son espace.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button type="submit" data-testid="action-submit-button" disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              {loading ? 'Enregistrement...' : 'Programmer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddActionDialog;
